// @spec DHB-STR-023
'use client';

import { createFileRoute } from '@tanstack/react-router';

import { Skeleton } from '@/components/ui/skeleton';
import { TransformationRulesManager } from '@/features/datahub/integrations/components/transformation-rules-manager';
import { useOrganizationId } from '@/hooks/use-organization-id';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/datahub/integrations/transformation-rules')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.transformationRules'),
  }),
  component: TransformationRulesPage,
});

/**
 * Transformation rules management page.
 *
 * Shows a list of reusable transformation rules that can be
 * applied to field mappings across integrations.
 */
function TransformationRulesPage() {
  const orgId = useOrganizationId();
  const { isAdmin, isLoading: isLoadingRole } = useOrgRole();

  // Show skeleton while loading org info
  if (isLoadingRole || !orgId) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-7xl space-y-6">
          <header>
            <Skeleton className="h-9 w-64" />
            <Skeleton className="mt-2 h-5 w-96" />
          </header>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">Transformation Rules</h1>
          <p className="mt-1 text-muted-foreground">
            Create and manage reusable transformation rules for field mappings
          </p>
        </header>

        <TransformationRulesManager isReadOnly={!isAdmin} />
      </div>
    </div>
  );
}
